import { Maybe, fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import Select from 'react-select';
import { Link } from '../../../../Resource/Types';
import { T, TPlainTextKey } from '../../../../Translations';
import Label from '../../../Form/Label';
import {
  GroupParticipantsResource,
  ParticipantResource,
} from '../../../People/Group/GroupParticipantsStore/Types';
import FormattedOptionLabel from './FormattedOptionLabel';
import { isOptionDisabled } from './IsOption';
import NoOptionsMessage from './NoOptionsMessage';
import { createStylesConfig } from './Styles';
import { useParticipantDynamicTypeaheadSelect } from './UseParticipantDynamicTypeaheadSelect';
import { FilterOption, State, emptyOption } from './UseParticipantDynamicTypeaheadSelect/Types';

export interface Props {
  newGroups: ReadonlyArray<GroupParticipantsResource>;
  newParticipants: ReadonlyArray<ParticipantResource>;
  name: string;
  endpoint: Link;
  label: TPlainTextKey;
  labelClassname: string;
  placeholder?: TPlainTextKey;
  initialValue: Maybe<FilterOption>;
  onChange: (value: FilterOption) => void;
}

export const valid = (state: State): boolean => {
  switch (state.kind) {
    case 'error':
      return false;
    case 'filtered':
    case 'filtering':
    case 'loading':
    case 'option-selected':
    case 'ready':
      return true;
  }
};

function ParticipantDynamicTypeaheadSelect({
  newGroups,
  newParticipants,
  name,
  endpoint,
  label,
  labelClassname,
  placeholder,
  initialValue,
  onChange,
}: Props) {
  const {
    state,
    handleInputChange,
    handleSelectionChange,
    isLoading,
    options,
    holder,
    selectedValue,
  } = useParticipantDynamicTypeaheadSelect(
    endpoint,
    onChange,
    initialValue,
    fromNullable(placeholder).getOrElseValue('Type to search...'),
  );

  const stylesConfig = createStylesConfig(valid(state));

  switch (state.kind) {
    case 'error':
      return (
        <div className="text-red-500 text-sm mt-2">
          <T kind={state.message} />
        </div>
      );
    case 'loading':
    case 'filtered':
    case 'filtering':
    case 'option-selected':
    case 'ready':
      return (
        <>
          <div className="min-w-[200px]">
            <Label htmlFor={name} labelText={label} className={labelClassname} />
            <Select
              formatOptionLabel={(filterOption) => (
                <FormattedOptionLabel filterOption={filterOption} />
              )}
              name={name}
              inputId={name}
              aria-label="Select Participants or Groups"
              value={selectedValue.getOrElseValue(emptyOption)}
              styles={stylesConfig}
              options={options}
              onChange={handleSelectionChange}
              isLoading={isLoading}
              isSearchable={true}
              placeholder={holder}
              onInputChange={handleInputChange}
              filterOption={() => true}
              menuPortalTarget={document.body}
              isOptionDisabled={isOptionDisabled(newParticipants, newGroups)}
              noOptionsMessage={({ inputValue }) => (
                <NoOptionsMessage inputValue={inputValue} state={state} />
              )}
            />
          </div>
        </>
      );
  }
}

export default observer(ParticipantDynamicTypeaheadSelect);
